import type { OutgoingAction } from '../../services/workQueue';
import type {
    OpenVirtualAppointmentsNowResult,
    VirtualAppointmentDto
} from '../../services/virtualAppointments';
import type { ToastType } from '../../providers/ToastProvider';
import { formatVaCount } from './openNowSubmit.ts';

export const OPEN_NOW_PENDING_MESSAGE =
    "VAs saved, but they couldn't open yet. They open on the next automatic run.";
export const MOVE_PENDING_MESSAGE =
    "Couldn't move the patient to the VA step yet. The next automatic run will move them.";
export const MOVE_NOT_DONE_MESSAGE =
    "The patient wasn't moved to a VA step. Refresh to check their VAs.";

export interface OpenNowFeedback {
    message: string;
    tone: ToastType;
}

// A submit without new VAs only moves the patient on with the ones already open.
function isMoveOnly(result: OpenVirtualAppointmentsNowResult): boolean {
    return result.virtualAppointments.length === 0;
}

function isStillPlanned(va: VirtualAppointmentDto): boolean {
    return va.status === 'planned';
}

// The completion page's entry when the submit moved the patient into a VA step.
export function openedNowCompletion(
    result: OpenVirtualAppointmentsNowResult
): OutgoingAction | null {
    const { outcome } = result;
    if (outcome?.reason !== 'moved' || !outcome.toStatus) return null;
    const opened = result.virtualAppointments.some((va) => !isStillPlanned(va));
    return {
        action: 'open-vas-now',
        label: opened ? 'VAs opened' : 'Moved to VA step',
        actor: 'internal',
        direction: 'lateral',
        to: outcome.toStatus
    };
}

// "2 VAs planned"; a planned VA already due opens at once: "2 VAs added, 1 opened now".
function plannedMessage(created: readonly VirtualAppointmentDto[]): string {
    const opened = created.filter((va) => !isStillPlanned(va)).length;
    return opened === 0
        ? `${formatVaCount(created.length)} planned`
        : `${formatVaCount(created.length)} added, ${opened} opened now`;
}

function moveOnlyFeedback(result: OpenVirtualAppointmentsNowResult): OpenNowFeedback | null {
    if (!result.outcome) return { message: MOVE_PENDING_MESSAGE, tone: 'error' };
    return result.outcome.reason === 'moved'
        ? null
        : { message: MOVE_NOT_DONE_MESSAGE, tone: 'error' };
}

// What to tell staff when the patient didn't move; null when the list itself says enough.
export function openNowFeedback(result: OpenVirtualAppointmentsNowResult): OpenNowFeedback | null {
    if (isMoveOnly(result)) return moveOnlyFeedback(result);
    if (result.outcome?.reason === 'moved') return null;
    const created = result.virtualAppointments;
    if (created.some((va) => va.triggerKind === 'now')) {
        return result.outcome ? null : { message: OPEN_NOW_PENDING_MESSAGE, tone: 'error' };
    }
    return { message: plannedMessage(created), tone: 'success' };
}

// "Tx - 2. DM VA needed" -> "DM VA needed".
function stepName(status: string): string {
    return status.replace(/^\w+ - [\d.]+\.\s*/, '');
}

// The toast for an "Add VAs" entry with no completion page: the step it moved to, else the feedback.
export function addVasToast(result: OpenVirtualAppointmentsNowResult): OpenNowFeedback | null {
    const completed = openedNowCompletion(result);
    if (completed?.to) {
        return { message: `${completed.label}: now in ${stepName(completed.to)}`, tone: 'success' };
    }
    return openNowFeedback(result);
}
